import { useState, type FormEvent } from 'react'
import { useFetch } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import { AsyncState, Button, Field, Input, PageHead, SectionHead, Select } from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { dateTime, dateInputToMs, msToDateInput } from '@/lib/format'

const DAY_MS = 24 * 60 * 60 * 1000
const DEFAULT_LIMIT = 200

// Часто встречающиеся типы событий журнала (kind — свободный TEXT, см. схему).
const KIND_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Все типы' },
  { value: 'shift', label: 'Смены' },
  { value: 'sync', label: 'Синхронизация' },
  { value: 'tariff', label: 'Тарифы' },
  { value: 'service', label: 'Сервис столов' },
  { value: 'receipt', label: 'Правки чеков' },
  { value: 'inventory', label: 'Инвентарь' },
  { value: 'customer', label: 'Клиенты' }
]

/** Строка журнала аудита (GET /v1/journal). Зеркало бэкенда — не импортируем shared. */
interface JournalRow {
  id: string
  ts: number
  kind: string
  message: string
  userName: string | null
}

export function Journal(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Аудит"
        title="Журнал"
        lead="События клуба: открытие и закрытие смен, правки конфигурации, синхронизация и другие действия сотрудников."
      />
      <ClubGate>
        <JournalList />
      </ClubGate>
    </>
  )
}

function JournalList(): React.JSX.Element {
  const query = useConfigQuery()

  const now = Date.now()
  const [fromStr, setFromStr] = useState(() => msToDateInput(now - 7 * DAY_MS))
  const [toStr, setToStr] = useState(() => msToDateInput(now))
  const [kind, setKind] = useState('')

  // Применённые фильтры (отдельно от полей ввода — запрос уходит по «Применить»).
  const [applied, setApplied] = useState<{ from: number; to: number; kind: string }>(() => ({
    from: dateInputToMs(msToDateInput(now - 7 * DAY_MS)),
    to: dateInputToMs(msToDateInput(now)) + DAY_MS - 1,
    kind: ''
  }))

  const list = useFetch<JournalRow[]>(
    '/v1/journal',
    {
      ...(query ?? {}),
      from: applied.from,
      to: applied.to,
      kind: applied.kind || undefined,
      limit: DEFAULT_LIMIT
    },
    [query?.clubId, applied.from, applied.to, applied.kind]
  )

  const onApply = (e: FormEvent): void => {
    e.preventDefault()
    setApplied({
      from: dateInputToMs(fromStr),
      // конец дня «по» включительно
      to: dateInputToMs(toStr) + DAY_MS - 1,
      kind
    })
  }

  const rows = list.data ?? []

  return (
    <>
      <form className="toolbar" onSubmit={onApply}>
        <div className="toolbar__left">
          <Field label="С даты" htmlFor="j-from">
            <Input
              id="j-from"
              type="date"
              value={fromStr}
              max={toStr}
              onChange={(e) => setFromStr(e.target.value)}
            />
          </Field>
          <Field label="По дату" htmlFor="j-to">
            <Input
              id="j-to"
              type="date"
              value={toStr}
              min={fromStr}
              onChange={(e) => setToStr(e.target.value)}
            />
          </Field>
          <Field label="Тип события" htmlFor="j-kind">
            <Select id="j-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" type="submit" disabled={list.loading}>
            {list.loading ? <span className="spin" /> : null}
            Применить
          </Button>
        </div>
      </form>

      <SectionHead title="События" meta={`${rows.length} зап.`} />

      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="За выбранный период событий нет."
        loadingLabel="Загружаем журнал…"
      />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Время</th>
                <th>Тип</th>
                <th>Сообщение</th>
                <th>Сотрудник</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{dateTime(r.ts)}</td>
                  <td>{r.kind}</td>
                  <td>{r.message}</td>
                  <td>{r.userName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
